import { removeDiacritics, hasAccessToThis } from 'services/Utilities'
import { t } from 'translations'
import { AccessType } from './hasAccessToThis'

type Route = {
  title?: string
  path: string
  icon?: string
  permission?: AccessType
  main?: string
  group?: 'CONFIGURATION' | 'TIME_PAY' | 'SCHEDULE' | 'PEOPLE'
  isMain?: boolean
  isMeringo?: boolean
  dontMatch?: string[]
  isAlpha?: boolean
  isBeta?: boolean
}

const getRoutes = (section: 'administration' | 'manager', sorted: boolean): Route[] | never => {
  const adminRoutes: Route[] = [
    // Settings
    { path: '/admin/settings', title: 'APPLICATION_SETTINGS', main: 'ApplicationSettings', icon: 'cog', permission: 'settingApplication' },
    { path: '/admin/settings/:type', main: 'ApplicationSetting', icon: 'cog', permission: 'settingApplication' },
    { path: '/admin/sync/:_id', main: 'Sync', icon: 'sync', isMain: true },

    // Configuration
    { path: '/admin/config/default', title: 'CONFIG_DEFAULT', main: 'ConfigDefault', icon: 'cogs', permission: 'configManage', group: 'CONFIGURATION' },
    { path: '/admin/config/filters', title: 'CONFIG_FILTER', main: 'ConfigFilterList', icon: 'filter', permission: 'configManage', group: 'CONFIGURATION' },
    { path: '/admin/config/filters/:_id', main: 'ConfigFilterForm', icon: 'filter', permission: 'configManage', group: 'CONFIGURATION' },
    { path: '/admin/config/lists', title: 'CONFIG_LISTS', main: 'ConfigListsList', icon: 'list-ol', permission: 'configManage', group: 'CONFIGURATION' },
    { path: '/admin/config/lists/:_id', main: 'ConfigListsForm', icon: 'list-ol', permission: 'configManage', group: 'CONFIGURATION' },
    {
      path: '/admin/config/procedures',
      title: 'CONFIG_PROCEDURE',
      main: 'ConfigProcedureList',
      icon: 'bars',
      permission: 'configManage',
      group: 'CONFIGURATION'
    },
    { path: '/admin/config/procedures/:_id', main: 'ConfigProcedureForm', icon: 'bars', permission: 'configManage', group: 'CONFIGURATION' },
    { path: '/admin/config/sorts', title: 'CONFIG_SORT', main: 'ConfigSortList', icon: 'sort-size-up', permission: 'configManage', group: 'CONFIGURATION' },
    { path: '/admin/config/sorts/:_id', main: 'ConfigSortForm', icon: 'sort-size-up', permission: 'configManage', group: 'CONFIGURATION' },
    {
      path: '/admin/permissions/group',
      title: 'PERMISSIONS',
      main: 'PermissionList',
      icon: 'sliders-h',
      permission: 'permissionManage',
      group: 'CONFIGURATION'
    },
    { path: '/admin/permissions/group/:_id', main: 'PermissionForm', icon: 'sliders-h', permission: 'permissionManage' },

    // Time tracking and pay
    { path: '/admin/breaks', title: 'BREAKS', main: 'BreakList', icon: 'coffee', permission: 'breakManage', group: 'TIME_PAY' },
    { path: '/admin/breaks/:_id', main: 'BreakForm', icon: 'coffee', permission: 'breakManage', group: 'TIME_PAY' },
    {
      path: '/admin/wagePremiums',
      title: 'WAGEPREMIUM',
      main: 'WagePremiumList',
      icon: 'barcode',
      permission: 'wagePremiumManage',
      group: 'TIME_PAY'
    },
    { path: '/admin/wagePremiums/:_id', main: 'WagePremiumForm', icon: 'barcode', permission: 'wagePremiumManage', group: 'TIME_PAY' },
    { path: '/admin/punchrules', title: 'PUNCH_RULES', main: 'PunchRuleList', icon: 'clock', permission: 'punchRuleManage', group: 'TIME_PAY' },
    { path: '/admin/punchrules/:_id', main: 'PunchRuleForm', icon: 'clock', permission: 'punchRuleManage', group: 'TIME_PAY' },
    {
      path: '/admin/requestoptions',
      title: 'REQUEST_OPTIONS',
      main: 'RequestOptionList',
      icon: 'paper-plane',
      permission: 'requestOptionManage',
      group: 'TIME_PAY'
    },
    { path: '/admin/requestoptions/:_id', main: 'RequestOptionForm', icon: 'paper-plane', permission: 'requestOptionManage', group: 'TIME_PAY' },

    // Schedule
    { path: '/admin/positions', title: 'POSITIONS', main: 'PositionList', icon: 'map-pin', permission: 'positionManage', group: 'SCHEDULE' },
    { path: '/admin/positions/:_id', main: 'PositionForm', icon: 'map-pin', permission: 'positionManage', group: 'SCHEDULE' },
    { path: '/admin/structure', title: 'STRUCTURE', main: 'Structure', icon: 'stream', permission: 'structureManage', group: 'SCHEDULE' },
    { path: '/admin/scheduletypes', title: 'SCHEDULE_TYPES', main: 'ScheduleTypeList', icon: 'calendar', permission: 'scheduleTypeManage', group: 'SCHEDULE' },
    { path: '/admin/scheduletypes/:_id', main: 'ScheduleTypeForm', icon: 'calendar', permission: 'scheduleTypeManage', group: 'SCHEDULE' },
    { path: '/admin/tags', title: 'TAGS', main: 'TagList', icon: 'tags', permission: 'tagManage', group: 'SCHEDULE' },
    { path: '/admin/tags/:_id', main: 'TagForm', icon: 'tags', permission: 'tagManage', group: 'SCHEDULE' },
    { path: '/admin/holidays', title: 'HOLIDAYS', main: 'HolidayList', icon: 'island-tropical', permission: 'holidayManage', group: 'SCHEDULE' },
    { path: '/admin/holidays/:_id', main: 'HolidayForm', icon: 'island-tropical', permission: 'holidayManage', group: 'SCHEDULE' },

    // People
    { path: '/admin/attributes', title: 'ATTRIBUTES', main: 'AttributeList', icon: 'clipboard-list', permission: 'attributeManage', group: 'PEOPLE' },
    { path: '/admin/attributes/:_id', main: 'AttributeForm', icon: 'clipboard-list', permission: 'attributeManage', group: 'PEOPLE' },
    { path: '/admin/hiringdata', title: 'HIRING_DATA', main: 'HiringDataList', icon: 'address-card', permission: 'hiringDataManage', group: 'PEOPLE' },
    { path: '/admin/hiringdata/:_id', main: 'HiringDataForm', icon: 'address-card', permission: 'hiringDataManage', group: 'PEOPLE' },
    { path: '/admin/teams', title: 'TEAMS', main: 'TeamList', icon: 'users-class', permission: 'teamManage', group: 'PEOPLE' },
    { path: '/admin/teams/:_id', main: 'TeamForm', icon: 'users-class', permission: 'teamManage', group: 'PEOPLE' },
    { path: '/admin/departments', title: 'DEPARTMENTS', main: 'DepartmentList', icon: 'building', permission: 'departmentManage', group: 'PEOPLE' },
    { path: '/admin/departments/:_id', main: 'DepartmentForm', icon: 'building', permission: 'departmentManage', group: 'PEOPLE' },

    // Recurring jobs testing
    {
      path: '/admin/integrations/status',
      title: 'INTEGRATION_STATUS',
      main: 'IntegrationStatusList',
      icon: 'rotate',
      permission: 'isMeringoAdmin',
      isMeringo: true
    },
    { path: '/admin/recurringJobsTests', title: 'TESTS', main: 'ReccuringJobsTests', icon: 'tasks', permission: 'isMeringoAdmin', isMeringo: true }
  ]

  const managerRoutes: Route[] = [
    { path: '/manage/attendances', title: 'ATTENDANCES', main: 'AttendanceList', icon: 'unlink', permission: 'manageAttendance' },
    { path: '/manage/attendances/:_id', main: 'AttendanceForm', permission: 'manageAttendance' },
    { path: '/manage/audit', title: 'AUDIT', main: 'Audit', icon: 'eye', permission: 'auditLogSee' },
    { path: '/manage/availabilities', title: 'AVAILABILITIES', main: 'AvailabilityList', icon: 'clock', permission: 'manageAvailability' },
    { path: '/manage/availabilities/import', main: 'AvailabilityImport', permission: 'manageAvailability' },
    { path: '/manage/availabilities/:_id', main: 'AvailabilityForm', permission: 'manageAvailability' },
    { path: '/manage/bulletins', title: 'BULLETINS', main: 'BulletinList', icon: 'bullhorn', permission: 'bulletinManage' },
    { path: '/manage/bulletins/:_id', main: 'BulletinForm', permission: 'bulletinManage' },
    { path: '/manage/contact/lists', title: 'CONTACT_LISTS', icon: 'clipboard-list', main: 'ContactAllLists', permission: 'contactListSee' },
    { path: '/manage/contact/lists/:_id', main: 'ContactSingleList', permission: 'contactListSee' },
    { path: '/manage/contact/templates/:_id', main: 'ContactNotificationTemplate', permission: 'contactNotifications' },
    { path: '/manage/punch/cards', title: 'PUNCH', main: 'PunchCardList', icon: 'chess-clock', permission: 'punchCardSee' },
    { path: '/manage/punch/cards/validation', main: 'PunchCardValidationList', permission: 'punchCardSee' },
    { path: '/manage/punch/cards/validation/:_id', main: 'PunchCardForm', permission: 'punchCardManage' },
    { path: '/manage/punch/cards/:_id', main: 'PunchCardForm', permission: 'punchCardManage' },
    { path: '/manage/punch/import', main: 'PunchImport', icon: 'chess-clock', permission: 'importPunch' },
    { path: '/manage/projects', title: 'PROJECTS', main: 'ProjectList', icon: 'book', permission: 'projectSee' },
    { path: '/manage/projects/import', main: 'ProjectImport', permission: 'projectManage' },
    { path: '/manage/projects/:_id', main: 'ProjectForm', permission: 'projectManage' },
    {
      path: '/manage/schedule',
      title: 'SCHEDULE',
      main: 'Schedule',
      icon: 'calendar',
      permission: 'scheduleSeeOthers',
      // Prevents /manage/schedule from matching /manage/schedule/templates in router
      dontMatch: ['/manage/schedule/templates']
    },
    { path: '/manage/schedule/capacity', main: 'ScheduleCapacity', permission: 'scheduleCapacity' },
    { path: '/manage/schedule/issues', main: 'ScheduleIssues', permission: 'scheduleSeeOthers' },
    { path: '/manage/schedule/import', main: 'ScheduleImport', permission: 'importSchedule' },
    { path: '/manage/schedule/templates', title: 'SCHEDULE_TEMPLATES', icon: 'calendar-plus', main: 'ScheduleTemplateList', permission: 'scheduleTemplateSee' },
    { path: '/manage/schedule/templates/import', main: 'ScheduleTemplateImport', permission: 'scheduleTemplateCreate' },
    { path: '/manage/projects/:projectId/templates/', main: 'ScheduleTemplateList', permission: 'scheduleTemplateSee' },
    { path: '/manage/schedule/templates/:_id', main: 'ScheduleTemplateForm', permission: 'scheduleTemplateCreate' },
    { path: '/manage/projects/:projectId/templates/:_id', main: 'ScheduleTemplateForm', permission: 'scheduleTemplateCreate' },
    { path: '/manage/timeoff', title: 'TIME_OFFS', main: 'TimeOffList', icon: 'plane', permission: 'manageTimeOff' },
    { path: '/manage/timeoff/import', main: 'TimeOffImport', permission: 'importTimeOff' },
    { path: '/manage/timeoff/:_id', main: 'TimeOffForm', permission: 'manageTimeOff' },
    { path: '/manage/reports', main: 'ReportList', icon: 'paperclip', permission: 'reportsSee' },
    { path: '/manage/reports/:type/:id', main: 'SingleReport', icon: 'paperclip', permission: 'reportsSee' },
    { path: '/manage/reports/new', title: 'REPORTS_LIST', main: 'RequestReport', icon: 'paperclip', permission: 'reportsSee' },
    { path: '/manage/users', title: 'USER_LIST', main: 'UserList', icon: 'users', permission: 'userSee' },
    { path: '/manage/users/import', main: 'UserImport', permission: 'importUser' },
    { path: '/manage/users/templates/:_id', main: 'UserTemplate', permission: 'manageUser' },
    { path: '/manage/users/sync/:_id?', main: 'UserSync', permission: 'importUser' },
    { path: '/manage/users/sync', main: 'UserSync', permission: 'importUser' },
    { path: '/manage/users/:_id', main: 'UserForm', permission: 'manageUser' }
  ]

  // Keep a seperate sorted:boolean array for navbar (we want to keep the same position in the array for the switch)
  if (section === 'administration') {
    if (sorted) return adminRoutes.sort((a, b) => sortLink(a, b, section))
    else return adminRoutes
  } else if (section === 'manager') {
    if (sorted) return managerRoutes.sort((a, b) => sortLink(a, b, section))
    else return managerRoutes
  }

  throw new Error(`Section doesn't exist`)
}

type SortLink = (a: Route, b: Route, section: string) => number

const sortLink: SortLink = (a, b, section) => {
  const x = removeDiacritics(t(`${section}.${a.title}`)).toLowerCase()
  const y = removeDiacritics(t(`${section}.${b.title}`)).toLowerCase()
  if (x < y) return -1
  if (x > y) return 1
  return 0
}

type ExtractFavorites = (routes: Route[], favorites: string[]) => { favoriteRoutes: Route[]; otherRoutes: Route[] }

export const extractFavorites: ExtractFavorites = (routes, favorites) => {
  if (routes.length <= 5) return { favoriteRoutes: routes, otherRoutes: [] }
  const favoriteRoutes = routes
    .filter(route => {
      if (!route.title || !route.permission || !hasAccessToThis(route.permission)) return false
      if (!favorites.includes(route.title)) return false
      return true
    })
    .sort((a, b) => {
      if (!a.title || !b.title) return -1

      return favorites.indexOf(a.title) < favorites.indexOf(b.title) ? -1 : 1
    })

  const otherRoutes = routes.filter(route => {
    if (!route.title || !route.permission || !hasAccessToThis(route.permission)) return false
    if (favorites.includes(route.title)) return false
    return true
  })

  return { favoriteRoutes, otherRoutes }
}

export default getRoutes
